import {
  EcgClassification,
  EcgLeadKey,
  IEcgRecording,
  IRawEcgWaveform,
  IRawEcgWaveformDevice,
} from "../../@types/Ecg";
import { detectEcgLeadIn } from "./detectEcgLeadIn";
import { normalizeEcgWaveform } from "./normalizeEcgWaveform";

interface IParseEcgMeta {
  id: string;
  classification?: EcgClassification;
  // From the API `configuration`: sampleInt16 * amplitudeScale = value in
  // amplitudeUnit. When provided, takes precedence over any unit embedded in
  // the waveform JSON; pass null/undefined to defer to the waveform's own scale.
  amplitudeScale?: number | null;
  amplitudeUnit?: string;
}

// Lead order as a clinician expects to read the limb leads.
const LEAD_ORDER: EcgLeadKey[] = ["I", "II", "III", "aVR", "aVL", "aVF"];

// ADC units per millivolt for legacy AliveCor recordings (amplitudeUnit "adc").
const ADC_UNITS_PER_MV = 2000;

// Factor to convert a value in `unit` into millivolts.
function unitToMv(unit: string | undefined): number {
  switch ((unit ?? "").toLowerCase()) {
    case "v":
      return 1000;
    case "mv":
      return 1;
    case "uv":
    case "µv":
      return 0.001;
    default:
      return 1;
  }
}

/**
 * Parse a raw AliveCor waveform recording into a display-ready structure.
 *
 * Sample-to-millivolt conversion:
 *  - If the caller passes `amplitudeScale` (from the API configuration), then
 *    mV = sample * amplitudeScale * unitToMv(amplitudeUnit).
 *  - Otherwise, if the waveform JSON declares `amplitudeUnit: "adc"`, samples
 *    are integer ADC units where 2000 units = 1 mV (mV = sample / 2000).
 *  - Otherwise samples are assumed to already be in millivolts.
 *
 * Only leads that the device actually recorded are returned, so a single-lead
 * recording yields one lead and a 6-lead recording yields six.
 *
 * Accepts either the on-device recorder JSON (the current standard) or the
 * older SDK format — both are normalized up front so the logic below only ever
 * sees the canonical shape.
 */
export function parseEcgData(
  rawInput: IRawEcgWaveform | IRawEcgWaveformDevice,
  meta: IParseEcgMeta
): IEcgRecording {
  const raw = normalizeEcgWaveform(rawInput);

  const frequencyHz = raw.sampleRateHz > 0 ? raw.sampleRateHz : 300;

  const toMv = (sample: number): number => {
    if (meta.amplitudeScale != null) {
      return sample * meta.amplitudeScale * unitToMv(meta.amplitudeUnit);
    }
    if (raw.amplitudeUnit === "adc") {
      return sample / (raw.adcUnitsPerMv ?? ADC_UNITS_PER_MV);
    }
    return sample;
  };

  const rawLeads = LEAD_ORDER.filter(
    (key) => (raw.waveform?.[key]?.length ?? 0) > 0
  ).map((key) => {
    const samples = raw.waveform?.[key] ?? [];

    return {
      key,
      label: key,
      samplesMv: samples.map(toMv),
    };
  });

  // Trim the device filter's settling transient at the start so the trace
  // begins at the true start of the test (see detectEcgLeadIn). The same cut
  // is applied to every lead to keep them time-aligned.
  const leadInSamples = detectEcgLeadIn(
    rawLeads.map((lead) => lead.samplesMv),
    frequencyHz
  );

  const leads = rawLeads.map((lead) => ({
    ...lead,
    samplesMv: lead.samplesMv.slice(leadInSamples),
  }));

  const sampleCount = leads.reduce(
    (max, lead) => Math.max(max, lead.samplesMv.length),
    0
  );

  return {
    id: meta.id,
    classification: meta.classification ?? "unknown",
    frequencyHz,
    durationSeconds: sampleCount / frequencyHz,
    sampleCount,
    heartRateBpm: raw.heartRateBpm ?? null,
    batteryPercent: raw.batteryPercent ?? null,
    leads,
    info: {
      capturedAt: raw.capturedAtIso ?? "",
      recordUuid: raw.sdkRecordUuid ?? "",
      platform: raw.source?.platform ?? "",
      appVersion: raw.source?.appVersion ?? "",
    },
  };
}
